<template>
  <aside
    class="alice-projsub"
    :class="{
      'alice-projsub--compact': compact && !collapsed,
      'alice-projsub--collapsed': collapsed,
    }"
    aria-label="Project navigation"
  >
    <button
      v-if="collapsed"
      class="alice-projsub__expand"
      type="button"
      aria-label="Expand project navigation"
      @click="$emit('toggle-collapsed')"
    >
      <Icon name="chevron-right" :size="14" />
    </button>

    <template v-if="!collapsed">
      <div class="alice-projsub__head">
        <div class="alice-projsub__eyebrow-row">
          <div class="alice-projsub__eyebrow">
            <span
              v-if="clientColor"
              class="alice-projsub__client-mark"
              :style="{ background: clientColor }"
            />
            <span class="alice-projsub__client-name">{{ clientName }}</span>
          </div>
          <div class="alice-projsub__eyebrow-actions">
            <button
              class="alice-projsub__menu-btn"
              aria-label="Collapse project navigation"
              @click="$emit('toggle-collapsed')"
            >
              <Icon name="chevron-left" :size="14" />
            </button>
            <button
              v-if="$slots.menu || showMenu"
              class="alice-projsub__menu-btn"
              aria-label="More actions"
              @click="$emit('menu')"
            >
              <Icon name="more" :size="18" />
            </button>
          </div>
        </div>

        <h2 class="alice-projsub__title">{{ title }}</h2>

        <!-- Read-only (guest) view: static status tag, no picker, no save. -->
        <div v-if="readonly && status" class="alice-projsub__status">
          <StatusTag :status="status" />
        </div>

        <v-menu v-if="!readonly && status" :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <button
              v-bind="menuProps"
              class="alice-projsub__status-trigger"
              type="button"
              title="Change status"
            >
              <StatusTag :status="status" />
              <Icon
                name="chevron-down"
                :size="14"
                class="alice-projsub__status-chev"
              />
            </button>
          </template>
          <v-list class="alice-menu-list">
            <v-list-item
              v-for="opt in availableStatuses"
              :key="opt.key"
              :active="opt.key === status"
              @click="onSelectStatus(opt.key)"
            >
              <template #prepend>
                <span
                  class="alice-projsub__status-swatch"
                  :class="`alice-projsub__status-swatch--${opt.key}`"
                />
              </template>
              <v-list-item-title>{{ opt.label }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <button
          v-if="!readonly"
          class="alice-projsub__save"
          :disabled="saveDisabled"
          @click="$emit('save')"
        >
          <span v-if="saving">Saving…</span>
          <span v-else>Save changes</span>
        </button>
      </div>

      <nav class="alice-projsub__tabs">
        <router-link
          v-for="tab in tabs"
          :key="tab.key"
          :to="`/project/${projectId}/${tab.key}`"
          class="alice-projsub__tab"
          active-class="alice-projsub__tab--active"
        >
          <span class="alice-projsub__tab-label">{{ tab.label }}</span>
          <span v-if="tab.count != null" class="alice-projsub__tab-count">{{
            tab.count
          }}</span>
        </router-link>
      </nav>

      <div class="alice-projsub__footer">
        <div v-if="createdLabel">Created {{ createdLabel }}</div>
        <div v-if="lastEditedLabel">Last edited {{ lastEditedLabel }}</div>
      </div>
    </template>
  </aside>
</template>

<script>
import Icon from "./Icon.vue";
import StatusTag from "./StatusTag.vue";

export default {
  name: "AliceProjectSubSidebar",
  components: { Icon, StatusTag },
  props: {
    projectId: { type: String, required: true },
    clientName: { type: String, default: "" },
    clientColor: { type: String, default: "" },
    title: { type: String, default: "" },
    status: { type: String, default: "" },
    saving: { type: Boolean, default: false },
    saveDisabled: { type: Boolean, default: false },
    tabs: {
      type: Array,
      default: () => [
        { key: "details", label: "Details" },
        { key: "obslog", label: "Obslog" },
        { key: "insights", label: "Insights" },
        { key: "chat", label: "Chat" },
        { key: "files", label: "Files" },
        { key: "recordings", label: "Recordings" },
      ],
    },
    createdLabel: { type: String, default: "" },
    lastEditedLabel: { type: String, default: "" },
    availableStatuses: {
      type: Array,
      default: () => [
        { key: "draft", label: "Draft" },
        { key: "in_progress", label: "In progress" },
        { key: "done", label: "Done" },
      ],
    },
    compact: { type: Boolean, default: false },
    collapsed: { type: Boolean, default: false },
    // Guest view: hide the save button and render the status as a static tag.
    readonly: { type: Boolean, default: false },
    // Render the "More actions" (⋯) button even without a #menu slot — the
    // parent handles the @menu event with its own v-menu.
    showMenu: { type: Boolean, default: false },
  },
  emits: ["save", "menu", "update-status", "toggle-collapsed"],
  methods: {
    onSelectStatus(key) {
      if (key === this.status) return;
      this.$emit("update-status", key);
    },
  },
};
</script>

<style scoped>
.alice-projsub {
  width: 100%;
  min-width: 0;
  flex-shrink: 0;
  background: var(--vp-white);
  border-right: 1px solid var(--border-hairline);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.alice-projsub--collapsed {
  align-items: center;
  padding-top: 12px;
}
.alice-projsub__expand {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid var(--border-hairline);
  cursor: pointer;
  color: var(--vp-black);
}
.alice-projsub__expand:hover {
  background: var(--bg-muted);
}
.alice-projsub__eyebrow-actions {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.alice-projsub__head {
  padding: 24px 24px 20px;
  border-bottom: 1px solid var(--border-hairline);
}
.alice-projsub__eyebrow-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.alice-projsub__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: var(--font-ui);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--fg-3);
  min-width: 0;
}
.alice-projsub__client-mark {
  width: 8px;
  height: 8px;
  flex-shrink: 0;
}
.alice-projsub__client-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.alice-projsub__menu-btn {
  width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: 1px solid transparent;
  border-radius: 0;
  cursor: pointer;
  color: var(--vp-black);
}
.alice-projsub__menu-btn:hover {
  border-color: var(--vp-black);
}

.alice-projsub__title {
  margin: 0 0 12px;
  font-family: var(--font-display);
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--vp-black);
}

.alice-projsub__status {
  margin-bottom: 16px;
}

.alice-projsub__status-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 6px 4px 0;
  margin-bottom: 12px;
  background: transparent;
  border: 1px solid transparent;
  cursor: pointer;
  border-radius: 0;
  color: var(--fg-3);
  transition: border-color 0.12s ease, color 0.12s ease;
}
.alice-projsub__status-trigger:hover {
  border-color: var(--vp-black);
  color: var(--vp-black);
  padding-left: 4px;
}
.alice-projsub__status-chev {
  color: inherit;
}

.alice-projsub__status-swatch {
  width: 10px;
  height: 10px;
  display: inline-block;
  margin-right: 8px;
  flex-shrink: 0;
}
.alice-projsub__status-swatch--draft {
  background: var(--vp-grijs-100);
}
.alice-projsub__status-swatch--in_progress {
  background: var(--vp-geel-100);
}
.alice-projsub__status-swatch--done {
  background: var(--vp-turquoise-100);
}

.alice-projsub__save {
  width: 100%;
  height: 44px;
  background: var(--vp-black);
  color: var(--vp-white);
  border: none;
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  border-radius: 0;
  position: relative;
  overflow: hidden;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover),
    transform var(--dur-press) var(--ease-out);
}
.alice-projsub__save:hover:not(:disabled) {
  background: var(--accent-100);
  color: var(--vp-black);
}
.alice-projsub__save:active:not(:disabled) {
  transform: scale(0.98);
}
.alice-projsub__save:disabled {
  opacity: 0.5;
  cursor: default;
}

.alice-projsub__tabs {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
  flex: 1;
  overflow-y: auto;
}
.alice-projsub__tab {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 24px;
  border-left: 3px solid transparent;
  margin-left: 0;
  font-family: var(--font-body);
  font-size: 15px;
  font-weight: 400;
  color: var(--fg-2);
  text-decoration: none;
  transition: background var(--dur-fast) var(--ease-hover),
    color var(--dur-fast) var(--ease-hover),
    transform var(--dur-press) var(--ease-out);
}
.alice-projsub__tab:active {
  transform: scale(0.98);
}
.alice-projsub__tab:hover {
  background: var(--bg-muted);
  color: var(--vp-black);
}
.alice-projsub__tab--active,
.alice-projsub__tab--active:hover {
  background: var(--vp-white);
  color: var(--vp-black);
  border-left-color: var(--vp-black);
  font-weight: 700;
}
.alice-projsub__tab-count {
  font-family: var(--font-ui);
  font-size: 12px;
  font-weight: 600;
  color: var(--fg-3);
}
.alice-projsub__tab--active .alice-projsub__tab-count {
  color: var(--vp-black);
}

.alice-projsub__footer {
  padding: 16px 24px 20px;
  border-top: 1px solid var(--border-hairline);
  font-family: var(--font-ui);
  font-size: 12px;
  color: var(--fg-3);
  line-height: 1.6;
}
</style>
